import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gauge, Fuel, Calendar, Settings2, ShieldCheck, ArrowRight, Calculator } from 'lucide-react';
import { estimateMonthly, isFinanceEligible } from '../utils/financing';

const statusConfig = {
  available: { className: 'badge-status badge-status--available', key: 'common.available', fallback: 'Disponible' },
  reserved: { className: 'badge-status badge-status--reserved', key: 'common.reserved', fallback: 'Réservé' },
  sold: { className: 'badge-status badge-status--sold', key: 'common.sold', fallback: 'Vendu' },
  coming_soon: { className: 'badge-status badge-status--soon', key: 'common.coming_soon', fallback: 'Bientôt' },
};

const formatPrice = (price) =>
  new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price || 0);

const formatMileage = (km) =>
  new Intl.NumberFormat('fr-BE').format(km || 0);

const formatMonthly = (v) =>
  new Intl.NumberFormat('fr-BE', { maximumFractionDigits: 0 }).format(Math.round(v || 0));

const CarCard = ({ car, showWarranty = true, priority = false }) => {
  const { t } = useTranslation();
  const sc = statusConfig[car.status] || statusConfig.available;
  const mainImg = car.image || (car.images && car.images[0]) || 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=900';
  const isSold = car.status === 'sold';
  const hasWarranty = car.warranty_included !== false; // default true

  const eligibility = isFinanceEligible({ price: car.price, year: car.year });
  const showFinancing = !isSold && car.status !== 'coming_soon' && eligibility.eligible;
  const monthly = showFinancing ? estimateMonthly(car.price).monthly : 0;

  return (
    <Link
      to={`/inventory/${car.id}`}
      className="car-card group"
      data-testid={`car-card-${car.id}`}
    >
      <div className="car-card-img-wrap">
        <img
          src={mainImg}
          alt={`${car.make} ${car.model}`}
          loading={priority ? 'eager' : 'lazy'}
          className="car-card-img"
        />
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2">
          <span className={sc.className}>{t(sc.key, sc.fallback)}</span>
          {car.is_latest_arrival && (
            <span className="badge-status bg-blue-900/95 text-white">
              {t('common.featured', 'Nouveauté')}
            </span>
          )}
        </div>
      </div>

      <div className="p-5 flex flex-col flex-grow">
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <h3 className="text-base sm:text-lg font-bold text-slate-900 line-clamp-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {car.make} {car.model}
          </h3>
          <span className="text-xs font-medium text-slate-500 whitespace-nowrap">{car.year}</span>
        </div>

        <p className="text-2xl font-extrabold text-blue-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {isSold ? <span className="text-slate-400 line-through text-lg">{formatPrice(car.price)}</span> : formatPrice(car.price)}
        </p>

        {/* Financing teaser — adapted per vehicle */}
        {showFinancing && (
          <div
            className="mb-3 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2"
            data-testid={`car-financing-teaser-${car.id}`}
            title={t('financing.teaser.tooltip')}
          >
            <Calculator size={13} className="text-emerald-600 flex-shrink-0" />
            <div className="flex flex-col leading-tight">
              <span
                className="text-emerald-800 text-sm font-bold"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t('card.fromPerMonth', { amount: formatMonthly(monthly) })}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                {t('card.financingLabel')}
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mt-1">
          <div className="flex items-center gap-1.5">
            <Gauge size={14} className="text-slate-400" />
            <span>{formatMileage(car.mileage)} km</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Fuel size={14} className="text-slate-400" />
            <span className="capitalize">{car.fuel}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar size={14} className="text-slate-400" />
            <span>{car.year}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Settings2 size={14} className="text-slate-400" />
            <span className="capitalize line-clamp-1">{car.transmission}</span>
          </div>
        </div>

        {showWarranty && hasWarranty && !isSold && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
            <span className="badge-trust">
              <ShieldCheck size={12} />
              {t('card.warranty', 'Garantie 1 an')}
            </span>
            <span className="text-blue-900 group-hover:text-blue-700 text-sm font-semibold inline-flex items-center gap-1 transition-colors">
              {t('card.details', 'Détails')}
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>
        )}
        {(!showWarranty || !hasWarranty) && !isSold && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-end">
            <span className="text-blue-900 group-hover:text-blue-700 text-sm font-semibold inline-flex items-center gap-1 transition-colors">
              {t('card.details', 'Détails')}
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>
        )}

        {/* Mandatory legal warning on financing */}
        {showFinancing && (
          <p
            className="mt-2 text-[10px] leading-tight text-slate-400 italic"
            data-testid={`car-financing-warning-${car.id}`}
          >
            {t('financing.warning')}
          </p>
        )}
      </div>
    </Link>
  );
};

export default CarCard;
